import type { TopCourseRow } from '@mentora/data-access'
import type { PriceFormatter } from '../price-formatter'
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { GraduationCap } from 'lucide-react'
import { getTranslations } from 'next-intl/server'

const statusVariants: Record<
  string,
  'default' | 'secondary' | 'outline' | 'success' | 'warning'
> = {
  draft: 'secondary',
  published: 'success',
  archived: 'warning',
}

export async function TopCoursesTable({ rows, formatPrice }: { rows: TopCourseRow[], formatPrice: PriceFormatter }) {
  const t = await getTranslations('dashboard.topCourses')
  const tStatus = await getTranslations('courses.status')

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t('description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
          <GraduationCap className="size-7" />
          <p className="text-sm">{t('empty')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">{t('columns.course')}</th>
                <th className="px-4 py-3 font-medium">{t('columns.status')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.enrollments')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.id}
                  data-testid={`top-courses-row-${row.id}`}
                  className="border-b border-border/40 last:border-b-0"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{row.title}</p>
                    <p className="text-xs text-muted-foreground">
                      /
                      {row.slug}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariants[row.status] ?? 'secondary'}>
                      {tStatus(row.status as 'draft' | 'published' | 'archived')}
                    </Badge>
                  </td>
                  <td data-testid="top-courses-enrollments" className="px-4 py-3 text-right font-mono tabular-nums text-muted-foreground">
                    {row.enrollCount}
                  </td>
                  <td data-testid="top-courses-revenue" className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                    {formatPrice(row.revenueCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
